import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePageMeta } from "../hooks/usePageMeta";

export default function ForgotPasswordPage() {
  usePageMeta("Reset Password | MacWay Estate Services", "Reset your MacWay dashboard password.");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[70vh] bg-sand flex items-center justify-center py-16 px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md bg-white border border-linen p-8 md:p-10">
        {sent ? (
          <div className="text-center" data-testid="forgot-success">
            <MailCheck className="w-12 h-12 text-forest mx-auto" />
            <h1 className="mt-5 text-3xl font-medium text-ink">Check your inbox</h1>
            <p className="mt-3 text-sm text-ink/65 leading-relaxed">
              If an admin account exists for <strong>{email}</strong>, we've sent a reset link. It works for 1
              hour. Don't see it? Check your spam or junk folder.
            </p>
            <Link to="/admin/login" className="btn-primary mt-8 w-full" data-testid="forgot-back-to-login">
              Back to Sign In
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-medium text-ink text-center" data-testid="forgot-heading">Reset your password</h1>
            <p className="mt-3 text-sm text-ink/60 text-center">
              Enter the email you use for the MacWay dashboard and we'll send you a reset link.
            </p>
            <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="forgot-password-form">
              <div>
                <label htmlFor="forgot-email" className="form-label">Email</label>
                <input
                  id="forgot-email"
                  type="email"
                  className="form-input"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                />
              </div>
              {error && (
                <p className="text-sm font-semibold text-red-700 bg-red-50 border border-red-200 px-4 py-3" role="alert" data-testid="forgot-error">
                  {error}
                </p>
              )}
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="forgot-submit">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </button>
            </form>
            <Link to="/admin/login" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-forest hover:text-brass transition-colors" data-testid="forgot-back-link">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
